import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCategoryName, searchPosts, formatDate } from "@/lib/content";
import type { Post } from "@/types/content";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const results: Post[] = keyword.trim() ? searchPosts(keyword).slice(0, 6) : [];

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    setKeyword("");
  }, [open]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  function goDetail(id: string) {
    onClose();
    navigate(`/post/${id}`);
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-24 md:pt-32">
      <div
        className="absolute inset-0 bg-foreground-950/45 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <div className="relative w-full max-w-2xl rounded-xl2 border border-background-200 bg-background-50 p-5 md:p-6 animate-fade-up">
        <div className="flex items-center gap-3 border-b border-background-200 pb-3">
          <i className="ri-search-line text-xl text-foreground-400"></i>
          <input
            ref={inputRef}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            type="text"
            placeholder="搜索名场面、爆笑高能、小甜瓜…"
            className="h-10 w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="mt-4 max-h-[52vh] overflow-y-auto">
          {keyword.trim() === "" && (
            <p className="py-6 text-center text-sm text-foreground-500">
              输入关键词，试试「名场面」「小甜瓜」「乐子」
            </p>
          )}
          {keyword.trim() !== "" && results.length === 0 && (
            <p className="py-6 text-center text-sm text-foreground-500">
              没有找到相关内容，换个关键词试试
            </p>
          )}
          <ul className="space-y-2">
            {results.map((post) => (
              <li key={post.id}>
                <button
                  type="button"
                  onClick={() => goDetail(post.id)}
                  className="flex w-full cursor-pointer items-center gap-4 rounded-lg border border-transparent px-3 py-3 text-left hover:border-background-200 hover:bg-background-100"
                >
                  <span className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img src={post.cover} alt={post.title} className="h-full w-full object-top object-cover" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground-950">{post.title}</span>
                    <span className="mt-1 block text-xs text-foreground-500">
                      {getCategoryName(post.categorySlug)} · {formatDate(post.date)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-3">
          <span className="text-xs text-foreground-500">热门搜索</span>
          {["名场面", "爆笑高能", "小甜瓜", "乐子合集"].map((word) => (
            <Link
              key={word}
              to={`/category/${word === "名场面" ? "mingchangmian" : word === "爆笑高能" ? "baoxiao" : word === "小甜瓜" ? "xiaotiangua" : "lezi"}`}
              onClick={onClose}
              className="rounded-full border border-background-200 px-3 py-1 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-700"
            >
              {word}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}